// Plan 08-04 — World container: the 4-layer scene graph root.
//
// Layer composition (back-to-front render order — last child paints on top):
//   1. BoardLayer    — 6×6 grid of cells + last-move shimmer (always rendered)
//   2. LetterLayer   — Runespring unified field (Letters view)
//   3. PieceLayer    — chess pieces (CHESS view)
//   4. EffectLayer   — particle emitters (motion-gated by Plan 08)
//
// DEC-023 + Phase 8 UI-SPEC §reduced-motion line 251: the board has two
// interchangeable views (Chess + Letters). Only one of LetterLayer/PieceLayer
// is mounted at a time so the two surfaces never visually stack. The 400ms
// 3D coin-flip transition is deferred — this is a straight conditional render.

import type { JSX, ReactNode } from 'react'
import type { BoardView } from './board-view'
import { BoardLayer } from './layers/BoardLayer'
import { EffectLayer } from './layers/EffectLayer'
import { LetterLayer } from './layers/LetterLayer'
import { PieceLayer } from './layers/PieceLayer'
import type { PlayerView } from './PlayerViewSchema'

interface WorldProps {
  playerView: PlayerView
  boardView: BoardView
  /**
   * Phase 31 Plan 31-03 — optional Pixi sub-layer rendered above LetterLayer
   * but below EffectLayer. Used by AnchorPlacementOverlay so
   * the Sun-Yellow scale-pulse + tap hit areas live in the shared Pixi
   * coordinate system. Mounts only in Letters view (Phase A only).
   */
  overlayLayer?: ReactNode
}

export function World({
  playerView,
  boardView,
  overlayLayer,
}: WorldProps): JSX.Element {
  return (
    <pixiContainer>
      {/* ARCA-749 (WS3) — BoardLayer + EffectLayer span the ACTIVE view's grid:
          the chess view is the fixed 6×6, the letters view is the growing word
          field. Both take boardView so the background wells + particle bursts
          match whichever layer (Piece/Letter) is mounted below. */}
      <BoardLayer playerView={playerView} boardView={boardView} />
      {boardView === 'letters' ? <LetterLayer playerView={playerView} /> : null}
      {boardView === 'chess' ? <PieceLayer playerView={playerView} /> : null}
      {/* Phase 31 Plan 31-03 — overlay slot sits ABOVE letter cubes so the
          Sun-Yellow border + hit areas overlay them. */}
      {boardView === 'letters' && overlayLayer ? overlayLayer : null}
      <EffectLayer playerView={playerView} boardView={boardView} />
    </pixiContainer>
  )
}
